import React, { useEffect, useRef, useState } from 'react';
import { useAppState } from 'src/shared/appState'; // Import useAppState
import { loadScript } from 'src/shared/helpers';

interface LoadScriptsProps {
    scriptUrls: string[]; // Array of URLs to load
    children: React.ReactNode; // Content to render once all scripts are loaded
    fallback: React.ReactNode; // Content to show while scripts are loading
    onProgress?: (progress: number) => void;
}

export const LoadScripts: React.FC<LoadScriptsProps> = ({ scriptUrls, children, fallback, onProgress }) => {
    const [scriptsLoaded, setScriptsLoaded] = useState(false);
    const { state, dispatch } = useAppState(); // Use the app state
    const onProgressRef = useRef(onProgress);

    useEffect(() => {
        onProgressRef.current = onProgress;
    }, [onProgress]);

    useEffect(() => {
        const loadAllScripts = async () => {
            const scriptsToLoad = scriptUrls.filter((url) => !state.loadedScripts.includes(url));

            if (scriptsToLoad.length === 0) {
                onProgressRef.current?.(100);
                setScriptsLoaded(true);
                return;
            }

            for (let i = 0; i < scriptsToLoad.length; i++) {
                await loadScript(scriptsToLoad[i]);
                dispatch({ type: 'ADD_LOADED_SCRIPT', scriptUrl: scriptsToLoad[i] });
                onProgressRef.current?.(((i + 1) / scriptsToLoad.length) * 100);
            }

            setScriptsLoaded(true);
        };

        if (scriptUrls.every((url) => state.loadedScripts.includes(url))) {
            onProgressRef.current?.(100);
            setScriptsLoaded(true);
        } else {
            loadAllScripts();
        }
    }, [scriptUrls, dispatch]);

    if (!scriptsLoaded) {
        return fallback;
    }

    return <>{children}</>; // Render children when scripts are fully loaded
};
